// Per-answer media for a vote statement: two pictures to pick between (tap to
// vote on phones) or two audio clips (players on the host screen only).
import { useT } from '../i18n';
import type { Attachment, SideMedia as SideMediaT, VoteChoice, VoteLabels } from '../shared/protocol';

export const hasSides = (s?: SideMediaT) => !!(s && (s.norm.url || s.norm.type || s.cringe.url || s.cringe.type));
export const sidesAreImages = (s?: SideMediaT) => !!s && [s.norm, s.cringe].some((a) => a.type === 'image');
export const sidesAreAudio = (s?: SideMediaT) => !!s && [s.norm, s.cringe].some((a) => a.type === 'audio');

/** Two pictures side by side. onPick makes them tappable (player voting). */
export function SidePictures({
  sides, labels, onPick, chosen, correct, disabled,
}: {
  sides: SideMediaT; labels: VoteLabels; onPick?: (c: VoteChoice) => void;
  chosen?: VoteChoice | null; correct?: VoteChoice; disabled?: boolean;
}) {
  const cell = (side: VoteChoice, a: Attachment, label: string) => {
    const cls = ['sidepic', side, chosen === side ? 'chosen' : '', chosen && chosen !== side ? 'dim' : '', correct === side ? 'correct' : ''].join(' ');
    const body = (
      <>
        {a.type === 'image' && a.url ? <img src={a.url} alt="" /> : <div className="sidepic-empty" />}
        <span className="sidepic-label">{label}{correct === side && ' ✓'}</span>
      </>
    );
    return onPick ? (
      <button type="button" key={side} className={cls} disabled={disabled} onClick={() => onPick(side)}>{body}</button>
    ) : (
      <div key={side} className={cls}>{body}</div>
    );
  };
  return <div className="sidepics">{cell('norm', sides.norm, labels.norm)}{cell('cringe', sides.cringe, labels.cringe)}</div>;
}

/** Host-only: one audio player per side, labelled with the button wording. */
export function SideAudioHost({ sides, labels, correct }: { sides: SideMediaT; labels: VoteLabels; correct?: VoteChoice }) {
  const row = (side: VoteChoice, a: Attachment, label: string) =>
    a.type === 'audio' && a.url ? (
      <div key={side} className={`sideaudio ${side} ${correct === side ? 'correct' : ''}`}>
        <span className="sideaudio-label">{label}{correct === side && ' ✓'}</span>
        <audio controls preload="auto" src={a.url} />
      </div>
    ) : null;
  return <div className="sideaudios">{row('norm', sides.norm, labels.norm)}{row('cringe', sides.cringe, labels.cringe)}</div>;
}

/** Player-side marker when the sides carry audio the phone will not play. */
export function SideAudioMarker() {
  const t = useT();
  return <div className="qaudio-marker">🎵 {t('play.listenHostBoth')}</div>;
}
